// Dashboard.jsx
// Main layout — sidebar + active view content

import React, { useEffect } from 'react';
import Header          from './Header';
import Sidebar         from './Sidebar';
import ChartsView      from './ChartsView';
import useStore        from '../store';
import { fetchWatchlist } from '../api';
import TradesView      from './TradesView';
import EMAReport      from './EMAReport';
import WatchlistManager from './WatchlistManager';
import StockMaster      from './StockMaster';
import Settings         from './Settings';
import ExchangeManager  from './ExchangeManager';
import AlertsView from './AlertsView';
// Add import at top
import ReportView from '../report_builder/ReportView';
import EMAStrategyReport from './EMAStrategyReport';



// ChartsView is extracted to ChartsView.jsx — imported below

// ============================================================
// PLACEHOLDER VIEWS
// ============================================================
function ReportEMACrossoverView() {
  return (
    <div style={styles.placeholder}>
      <h2>EMA Crossover Report</h2>
      <p>Coming soon</p>
    </div>
  );
}

function ReportSRView() {
  return (
    <div style={styles.placeholder}>
      <h2>Support / Resistance Report</h2>
      <p>Coming soon</p>
    </div>
  );
}

// ============================================================
// VIEW ROUTER
// ============================================================


const VIEW_MAP = {
  charts:               <ChartsView />,
  watchlists:           <WatchlistManager />,
  stocks:               <StockMaster />,
  exchanges:            <ExchangeManager />,
  trades:               <TradesView />,
  settings:             <Settings />,
  report_ema_crossover: <EMAReport />,
  report_sr:            <ReportSRView />,
  live_alerts:          <AlertsView />,
  // Add to VIEW_MAP
  report_builder:       <ReportView />,
  report_ema_strategy:  <EMAStrategyReport />,

};

// ============================================================
// DASHBOARD
// ============================================================
function Dashboard() {
  const { setWatchlist, setSelectedSymbol, activeView } = useStore();

  useEffect(() => {
    const loadWatchlist = async () => {
      try {
        const data = await fetchWatchlist();
        if (data.has_default && data.watchlist.length > 0) {
          setWatchlist(data.watchlist);
          setSelectedSymbol(data.watchlist[0]);
        }
      } catch (err) {
        console.error('Error loading watchlist:', err);
      }
    };
    loadWatchlist();
  }, []);

  return (
    <div style={styles.container}>
      <Header />
      <div style={styles.body}>
        <Sidebar />
        <div style={styles.content}>
          {VIEW_MAP[activeView] || <ChartsView />}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// STYLES
// ============================================================
const styles = {
  container: {
    display:       'flex',
    flexDirection: 'column',
    height:        '100vh',
    overflow:      'hidden',
  },
  body: {
    display:  'flex',
    flex:     1,
    overflow: 'hidden',
  },
  content: {
    flex:     1,
    overflow: 'hidden',
    display:  'flex',
    minWidth: 0,
  },
  viewContainer: {
    display:       'flex',
    flexDirection: 'column',
    flex:          1,
    overflow:      'hidden',
  },
  layoutBar: {
    display:         'flex',
    alignItems:      'center',
    gap:             '6px',
    padding:         '4px 16px',
    backgroundColor: '#ffffff',
    borderBottom:    '1px solid #e0e0e0',
    flexShrink:      0,
  },
  label: {
    color:    '#666',
    fontSize: '12px',
  },
  btn: {
    padding:         '3px 10px',
    backgroundColor: '#f0f0f0',
    color:           '#666',
    border:          '1px solid #ddd',
    borderRadius:    '4px',
    cursor:          'pointer',
    fontSize:        '12px',
  },
  btnActive: {
    backgroundColor: '#2962ff',
    color:           '#ffffff',
    borderColor:     '#2962ff',
  },
  chartArea: {
    display:  'flex',
    flex:     1,
    overflow: 'hidden',
    minWidth: 0,
  },
  single: {
    flex:     1,
    padding:  '4px',
    overflow: 'hidden',
    display:  'flex',
    minWidth: 0,
  },
  // 2 charts — side by side
  grid2: {
    display:             'grid',
    gridTemplateColumns: '1fr 1fr',
    gridTemplateRows:    '1fr',
    flex:                1,
    gap:                 '4px',
    padding:             '4px',
    backgroundColor:     '#f5f5f5',
    overflow:            'hidden',
  },
  // 3 charts — left col: 2 stacked, right col: 1 tall
  grid3: {
    display:             'grid',
    gridTemplateColumns: '1fr 1fr',
    gridTemplateRows:    '1fr 1fr',
    flex:                1,
    gap:                 '4px',
    padding:             '4px',
    backgroundColor:     '#f5f5f5',
    overflow:            'hidden',
  },
  // 4 charts — 2x2
  grid4: {
    display:             'grid',
    gridTemplateColumns: '1fr 1fr',
    gridTemplateRows:    '1fr 1fr',
    flex:                1,
    gap:                 '4px',
    padding:             '4px',
    backgroundColor:     '#f5f5f5',
    overflow:            'hidden',
  },
  // Chart slot wrapper — fills its grid cell
  chartSlot: {
    overflow:  'hidden',
    minWidth:  0,
    minHeight: 0,
    display:   'flex',
  },
  // 3-chart layout: right column spanning both rows
  // Charts 0 and 1 are in left col (row 1 and row 2)
  // Chart 2 is in right col spanning row 1 and row 2
  spanRight: {
    gridColumn:  2,
    gridRow:     '1 / 3',
    overflow:    'hidden',
    minWidth:    0,
    minHeight:   0,
    display:     'flex',
  },
  placeholder: {
    flex:           1,
    display:        'flex',
    flexDirection:  'column',
    alignItems:     'center',
    justifyContent: 'center',
    color:          '#999',
    gap:            '12px',
  },
  noDefault: {
    flex:           1,
    display:        'flex',
    flexDirection:  'column',
    alignItems:     'center',
    justifyContent: 'center',
    gap:            '12px',
    color:          '#666',
  },
  noDefaultIcon: {
    fontSize: '48px',
  },
  noDefaultTitle: {
    fontSize:   '18px',
    fontWeight: 'bold',
    color:      '#444',
  },
  noDefaultText: {
    fontSize:  '14px',
    color:     '#888',
    textAlign: 'center',
    maxWidth:  '320px',
    lineHeight: '1.6',
  },
};

export default Dashboard;